import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../layout/Header';
import Footer from '../sections/Footer';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiAlertTriangle, FiHeart, FiShield, FiSearch, FiClock, 
  FiLock, FiDollarSign, FiGift, FiSmartphone, FiMail, 
  FiUser, FiShoppingBag, FiTrendingUp, FiGlobe, FiBriefcase, 
  FiActivity, FiHome, FiCreditCard, FiBitcoin, FiCalendar,
  FiMap, FiMessageSquare, FiStar, FiThumbsUp, FiUsers
} = FiIcons;

const TypesOfScamsPage = () => {
  // Scam categories with icons and descriptions
  const scamCategories = [
    {
      title: "Romance Scams",
      icon: FiHeart,
      description: "Criminals create fake profiles on dating apps to build relationships with victims before requesting money.",
      path: "/scams/romance-scams",
      color: "from-pink-500 to-red-500",
    },
    {
      title: "Phishing Schemes",
      icon: FiMail,
      description: "Fraudulent attempts to obtain sensitive information by disguising as trustworthy entities via email or text.",
      path: "/scams/phishing-schemes",
      color: "from-blue-500 to-cyan-500",
    },
    {
      title: "Tech Support Scams",
      icon: FiSmartphone,
      description: "Scammers pose as tech support representatives to gain remote access to your devices and personal information.",
      path: "/scams/tech-support-scams",
      color: "from-purple-500 to-indigo-500",
    },
    {
      title: "Fake Sweepstakes & Lottery Scams",
      icon: FiGift,
      description: "You're told you've won a prize but must pay fees upfront to claim your non-existent winnings.",
      path: "/scams/fake-sweepstakes-lottery-scams",
      color: "from-yellow-500 to-amber-500",
    },
    {
      title: "Impersonation Scams",
      icon: FiUser,
      description: "Criminals pretend to be government officials, family members, or trusted organizations to steal money or information.",
      path: "/scams/impersonation-scams",
      color: "from-red-500 to-orange-500",
    },
    {
      title: "Online Shopping Scams",
      icon: FiShoppingBag,
      description: "Fake websites, bogus listings, and counterfeit products designed to steal your money with no product delivered.",
      path: "/scams/online-shopping-scams",
      color: "from-green-500 to-emerald-500",
    },
    {
      title: "Investment Scams",
      icon: FiTrendingUp,
      description: "Promises of high returns with little or no risk that leave investors with empty pockets.",
      path: "/scams/investment-scams",
      color: "from-blue-500 to-indigo-500",
    },
    {
      title: "Charity Scams",
      icon: FiGlobe,
      description: "Fake charities that exploit your generosity, especially following disasters or emergencies.",
      path: "/scams/charity-scams",
      color: "from-purple-500 to-pink-500",
    },
    {
      title: "Employment Scams",
      icon: FiBriefcase,
      description: "False job opportunities that require payment or personal information before you can start 'working.'",
      path: "/scams/employment-scams",
      color: "from-cyan-500 to-blue-500",
    },
    {
      title: "Healthcare & Insurance Scams",
      icon: FiActivity,
      description: "Fraudsters offering fake health insurance or medical services to steal your information and money.",
      path: "/scams/healthcare-insurance-scams",
      color: "from-teal-500 to-green-500",
    },
    {
      title: "Rental & Real Estate Scams",
      icon: FiHome,
      description: "Listings for properties that don't exist or aren't available, designed to collect deposits or application fees.",
      path: "/scams/rental-real-estate-scams",
      color: "from-amber-500 to-orange-500",
    },
    {
      title: "Debt Relief & Loan Scams",
      icon: FiCreditCard,
      description: "False promises to reduce or eliminate your debt while charging high fees and delivering no results.",
      path: "/scams/debt-relief-loan-scams",
      color: "from-red-500 to-pink-500",
    },
    {
      title: "Cryptocurrency Scams",
      icon: FiBitcoin,
      description: "Fake investment opportunities, exchanges, and wallets targeting those interested in digital currency.",
      path: "/scams/cryptocurrency-scams",
      color: "from-orange-500 to-amber-500",
    },
    {
      title: "Subscription Scams",
      icon: FiCalendar,
      description: "Misleading free trials that turn into expensive subscriptions with hidden terms and difficult cancellation processes.",
      path: "/scams/subscription-scams",
      color: "from-indigo-500 to-purple-500",
    },
    {
      title: "Travel Scams",
      icon: FiMap,
      description: "Too-good-to-be-true vacation deals that leave travelers with invalid bookings or unexpected charges.",
      path: "/scams/travel-scams",
      color: "from-blue-500 to-teal-500",
    },
    {
      title: "Puppy Scams",
      icon: FiHeart,
      description: "Fake pet listings that require payment upfront for animals that are never delivered.",
      path: "/scams/puppy-scams",
      color: "from-green-500 to-teal-500",
    },
    {
      title: "Online Dating Extortion",
      icon: FiMessageSquare,
      description: "Blackmail schemes where scammers threaten to expose personal conversations or intimate images.",
      path: "/scams/online-dating-extortion",
      color: "from-pink-500 to-purple-500",
    },
    {
      title: "Fake Reviews & Ratings",
      icon: FiThumbsUp,
      description: "Misleading product or service reviews designed to influence purchasing decisions.",
      path: "/scams/fake-reviews-ratings",
      color: "from-yellow-500 to-orange-500",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Header />

      {/* Hero Section */}
      <section className="relative py-20 min-h-[50vh] flex items-center justify-center overflow-hidden">
        {/* NYC Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
            alt="New York City Skyline"
            className="w-full h-full object-cover"
          />
          {/* Dark gradient overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-purple-900/80 to-gray-900/90 z-10"></div>
        </div>

        <div className="container mx-auto px-6 relative z-30">
          <motion.div
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1
              className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-[#ffc700] to-cyan-400 bg-clip-text text-transparent"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              Types of Scams
            </motion.h1>
            <motion.p
              className="text-xl md:text-2xl mb-8 text-gray-300 font-medium"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Knowledge is your best defense. Learn to identify and avoid common scams.
            </motion.p>

            <motion.div
              className="flex items-center justify-center gap-4 mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <div className="flex items-center gap-2 bg-red-600 px-4 py-2 rounded-full">
                <SafeIcon icon={FiAlertTriangle} className="text-white" />
                <span className="text-white font-bold">Stay Alert</span>
              </div>
              <div className="flex items-center gap-2 bg-yellow-600 px-4 py-2 rounded-full">
                <SafeIcon icon={FiSearch} className="text-white" />
                <span className="text-white font-bold">Get Informed</span>
              </div>
              <div className="flex items-center gap-2 bg-green-600 px-4 py-2 rounded-full">
                <SafeIcon icon={FiShield} className="text-white" />
                <span className="text-white font-bold">Stay Protected</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Search & Filter Section */}
      <section className="py-8 bg-gray-800">
        <div className="container mx-auto px-6">
          <motion.div 
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-gray-900 p-6 rounded-xl border border-gray-700">
              <div className="flex flex-col md:flex-row gap-4 items-center">
                <div className="relative flex-grow">
                  <SafeIcon icon={FiSearch} className="absolute left-4 top-3.5 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search scam types..."
                    className="w-full bg-gray-800 text-white border border-gray-700 rounded-full py-3 pl-12 pr-4 focus:outline-none focus:border-[#ffc700]"
                  />
                </div>
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <SafeIcon icon={FiClock} className="text-[#ffc700]" />
                  <span className="text-gray-300">Most Common</span>
                </div>
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <SafeIcon icon={FiLock} className="text-[#ffc700]" />
                  <span className="text-gray-300">Most Dangerous</span>
                </div>
                <div className="flex items-center gap-2 whitespace-nowrap">
                  <SafeIcon icon={FiDollarSign} className="text-[#ffc700]" />
                  <span className="text-gray-300">Highest Losses</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Scam Categories Grid */}
      <section className="py-16 bg-gradient-to-br from-gray-900 to-purple-900">
        <div className="container mx-auto px-6">
          <motion.div 
            className="mb-12 text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-[#ffc700] to-cyan-400 bg-clip-text text-transparent">
              Protect Yourself From These Common Scams
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Click on any scam type to learn more about how it works, warning signs to look for, and steps to protect yourself.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {scamCategories.map((category, index) => (
              <motion.div
                key={category.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                className="group"
              >
                <Link to={category.path} className="block h-full">
                  <div className="bg-gray-800 p-6 rounded-xl border border-gray-700 hover:border-[#ffc700]/50 transition-all duration-300 transform hover:scale-105 h-full flex flex-col">
                    <div className={`bg-gradient-to-br ${category.color} p-4 rounded-xl w-16 h-16 flex items-center justify-center mb-6`}>
                      <SafeIcon icon={category.icon} className="text-2xl text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-4 group-hover:text-[#ffc700] transition-colors">
                      {category.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed flex-grow">
                      {category.description}
                    </p>
                    <div className="mt-6 flex items-center text-[#ffc700] group-hover:translate-x-2 transition-transform">
                      <span className="font-medium">Learn more</span>
                      <SafeIcon icon={FiAlertTriangle} className="ml-2" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Report a Scam Section */}
      <section className="py-16 bg-gray-800">
        <div className="container mx-auto px-6">
          <motion.div
            className="max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-br from-gray-900 to-purple-900 p-8 md:p-12 rounded-2xl border border-[#ffc700]/30">
              <div className="flex flex-col md:flex-row gap-8 items-center">
                <div className="md:w-1/3">
                  <div className="bg-gradient-to-br from-[#ffc700] to-[#ffb700] p-6 rounded-full w-32 h-32 mx-auto flex items-center justify-center">
                    <SafeIcon icon={FiShield} className="text-5xl text-black" />
                  </div>
                </div>
                <div className="md:w-2/3">
                  <h3 className="text-3xl font-bold text-white mb-4">
                    Have You Been Scammed?
                  </h3>
                  <p className="text-gray-300 leading-relaxed mb-6">
                    If you've fallen victim to a scam, it's important to act quickly. Report the incident, protect your accounts, and get the support you need. Linda Roberts and the AVAH Outreach Group are here to help you through the recovery process.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link
                      to="/scam-assistance"
                      className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3 shadow-lg"
                    >
                      <SafeIcon icon={FiHeart} className="text-xl" />
                      Get Help Now
                    </Link>
                    <button
                      className="bg-transparent border-2 border-[#ffc700] hover:bg-[#ffc700] hover:text-black text-[#ffc700] px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3"
                    >
                      <SafeIcon icon={FiAlertTriangle} className="text-xl" />
                      Report a Scam
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Educational Resources */}
      <section className="py-16 bg-gradient-to-br from-purple-900 to-gray-900">
        <div className="container mx-auto px-6">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6 bg-gradient-to-r from-[#ffc700] to-cyan-400 bg-clip-text text-transparent">
              Stay Informed, Stay Safe
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Access these additional resources to build your scam awareness skills
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gray-800 p-6 rounded-xl border border-gray-700 hover:border-[#ffc700]/50 transition-all duration-300"
            >
              <div className="bg-gradient-to-br from-pink-500 to-purple-600 p-4 rounded-xl w-16 h-16 flex items-center justify-center mb-6">
                <SafeIcon icon={FiGlobe} className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">
                Linda's YouTube Channel
              </h3>
              <p className="text-gray-400 leading-relaxed mb-6">
                Watch real scam calls and learn how to identify scammer tactics in action. New videos weekly.
              </p>
              <a
                href="http://link.lrcs.nyc/YTSL"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#ffc700] font-medium hover:underline flex items-center"
              >
                Subscribe Now
                <SafeIcon icon={FiAlertTriangle} className="ml-2" />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gray-800 p-6 rounded-xl border border-gray-700 hover:border-[#ffc700]/50 transition-all duration-300"
            >
              <div className="bg-gradient-to-br from-cyan-500 to-blue-600 p-4 rounded-xl w-16 h-16 flex items-center justify-center mb-6">
                <SafeIcon icon={FiShield} className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">
                Scam Prevention Guide
              </h3>
              <p className="text-gray-400 leading-relaxed mb-6">
                Download our comprehensive guide with checklists, warning signs, and step-by-step protection strategies.
              </p>
              <button
                className="text-[#ffc700] font-medium hover:underline flex items-center"
              >
                Download Free Guide
                <SafeIcon icon={FiAlertTriangle} className="ml-2" />
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gray-800 p-6 rounded-xl border border-gray-700 hover:border-[#ffc700]/50 transition-all duration-300"
            >
              <div className="bg-gradient-to-br from-green-500 to-teal-600 p-4 rounded-xl w-16 h-16 flex items-center justify-center mb-6">
                <SafeIcon icon={FiUsers} className="text-2xl text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">
                Community Support
              </h3>
              <p className="text-gray-400 leading-relaxed mb-6">
                Join our community forums to share experiences, ask questions, and learn from others who have encountered scams.
              </p>
              <Link
                to="/scam-assistance"
                className="text-[#ffc700] font-medium hover:underline flex items-center"
              >
                Join the Community
                <SafeIcon icon={FiAlertTriangle} className="ml-2" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TypesOfScamsPage;